import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { getChatMessagesRequest, saveMessageRequest } from '../../../../api/hooks/chat/chatApi'
import { submitQueryFeedbackRequest } from '../../../../api/hooks/chat/chatFeedbackApi'
import { useSelector } from 'react-redux'
import { useState, useCallback, useEffect, useMemo } from 'react'

export const useMessages = (chatID, recentChatID, isPublic = false) => {
    const [messagesLimit, setMessagesLimit] = useState(20)
    const [messagesCursor, setMessagesCursor] = useState(null) // Start with null for initial load
    const [messagesOrder, setMessagesOrder] = useState('desc')

    // Single source of truth for ALL messages (backend + real-time)
    const [allMessages, setAllMessages] = useState([])
    const [hasMoreMessages, setHasMoreMessages] = useState(false)
    const userID = useSelector((state) => state.auth?.userID)
    const workspaceID = useSelector((state) => state.auth?.workspaceID)
    const queryClient = useQueryClient()

    // Mutation for saving messages to backend
    const { mutate: saveMessage } = useMutation({
        mutationFn: ({ chatID, content }) => saveMessageRequest(chatID, content),
        onError: (error) => {
            console.error('Failed to save message:', error)
        },
    })

    // Mutation for submitting query feedback
    const {
        mutate: submitFeedback,
        isLoading: isSubmittingFeedback,
        error: feedbackError,
    } = useMutation({
        mutationFn: ({ correlationID, feedbackType, userCorrection }) =>
            submitQueryFeedbackRequest(correlationID, feedbackType, userCorrection),
        onError: (error) => {
            console.error('Failed to submit feedback:', error)
        },
    })

    const {
        data: chatMessages,
        isLoading: loadingChatMessages,
        error: chatMessagesError,
        refetch: refetchChatMessages,
        isFetching: isFetchingMessages,
    } = useQuery({
        queryKey: [
            'messages',
            chatID,
            userID,
            workspaceID,
            messagesLimit,
            messagesCursor,
            messagesOrder,
        ],
        queryFn: () =>
            getChatMessagesRequest(
                chatID,
                userID,
                workspaceID,
                messagesLimit,
                messagesCursor,
                messagesOrder
            ),
        enabled: !!(recentChatID !== chatID && chatID && userID && workspaceID && !isPublic),
        keepPreviousData: true, // Keep previous data while loading more
    })

    // Handle backend messages and accumulate them
    useEffect(() => {
        if (chatMessages?.messages) {
            if (messagesCursor === null) {
                // Initial load - replace all messages
                setAllMessages(chatMessages.messages)
            } else {
                // Load more - prepend older messages to the beginning
                setAllMessages((prev) => [...prev, ...chatMessages.messages])
            }

            // Update pagination state for next potential load
            setHasMoreMessages(chatMessages.hasMore || false)
        }
    }, [chatMessages, messagesCursor])

    // Function to invalidate messages for a specific chat
    const invalidateMessages = useCallback(
        (targetChatID) => {
            if (!targetChatID || !userID || !workspaceID || isPublic) return

            queryClient.invalidateQueries({
                queryKey: [
                    'messages',
                    targetChatID,
                    userID,
                    workspaceID,
                    messagesLimit,
                    messagesCursor,
                    messagesOrder,
                ],
            })
        },
        [queryClient, userID, workspaceID, messagesLimit, messagesCursor, messagesOrder, isPublic]
    )

    // Function to add a real-time message to the chat
    const addMessage = useCallback(
        (message, firstMessageChatID) => {
            // Add to local state immediately
            setAllMessages((prev) => [message, ...prev])

            // Save to backend if conditions are met
            const shouldSave =
                !isPublic &&
                !message.needsReasoning && // Exclude UI-only quick/deep analysis choice prompts
                (message.role === 'user' || message.role === 'scoop' || message.role === 'error') // Save user messages, scoop responses, and errors

            if (shouldSave && (chatID || firstMessageChatID)) {
                saveMessage({
                    chatID: chatID || firstMessageChatID,
                    content: {
                        ...message.rawData,
                        text: message.role === 'user' ? message.text : undefined,
                        role: message.role,
                        // Include original query for error messages (helps with debugging)
                        ...(message.role === 'error' &&
                            message.originalQuery && {
                                originalQuery: message.originalQuery,
                            }),
                    },
                })
            }
        },
        [chatID, isPublic, saveMessage]
    )

    // Function to load more messages (pagination)
    const loadMoreMessages = useCallback(() => {
        if (!hasMoreMessages || isFetchingMessages || !chatMessages?.nextCursor) return

        // Update cursor to trigger automatic refetch via queryKey dependency
        // React Query will automatically refetch when messagesCursor changes
        setMessagesCursor(chatMessages.nextCursor)
    }, [hasMoreMessages, isFetchingMessages, chatMessages?.nextCursor])

    // Function to clear all messages (for new chat)
    const clearAllMessages = useCallback(() => {
        setAllMessages([])
        setMessagesCursor(null)
        setHasMoreMessages(false)
    }, [])

    // Convert messages to UI format
    const convertedMessages = useMemo(() => {
        if (!allMessages || allMessages.length === 0) return []

        // Reverse DESC order from backend to get chronological order for chat UI
        return [...allMessages].reverse().map((msg) => {
            // Real-time messages have numeric messageIDs, backend messages have "msg_..." messageIDs
            // If it's a real-time message, return as-is since it's already in UI format
            if (msg.messageID && !String(msg.messageID).startsWith('msg_')) return msg

            // Handle backend/database messages with nested structure
            const role = msg.content?.role || msg.role || 'assistant'

            // Handle user messages from backend
            if (role === 'user') {
                return {
                    messageID: msg.messageID,
                    text: msg.content?.text || '',
                    role: 'user',
                    timestamp: msg.timestamp,
                }
            }

            // Handle AI messages with structured data from backend
            if (role === 'scoop' && msg.content) {
                // Extract text for display
                let messageText = ''
                if (msg.content.content?.text?.aiDescription) {
                    messageText = msg.content.content.text.aiDescription
                } else if (msg.content.content?.text?.description) {
                    messageText = msg.content.content.text.description
                }

                return {
                    messageID: msg.messageID,
                    role: 'scoop',
                    timestamp: msg.timestamp,
                    type: 'structured_response',
                    text: messageText,

                    // Preserve all the rich structured data for rendering
                    header: msg.content.header,
                    content: msg.content.content,
                    footer: msg.content.footer,

                    // Store the complete backend response as rawData
                    rawData: msg.content,
                }
            }

            // Fallback for other message types
            return {
                messageID: msg.messageID,
                text: typeof msg.content === 'string' ? msg.content : 'Message',
                role: role,
                timestamp: msg.timestamp,
            }
        })
    }, [allMessages])

    return {
        // Loading states
        loadingChatMessages,
        chatMessagesError,
        isFetchingMessages,

        // UI-ready messages
        convertedMessages,
        hasMoreMessages,

        // Message management functions
        addMessage,
        loadMoreMessages,
        invalidateMessages,
        clearAllMessages,

        // Feedback mutation
        submitFeedback,
        isSubmittingFeedback,
        feedbackError,
    }
}
